import { CloseOutlined } from '@mui/icons-material';
import { Box, Button, Divider, IconButton, Modal, Typography } from '@mui/material';
import { Location } from 'graphql/generated/graphqlTypes';
import useSortingAccessProfilesTableLockGroupsAndLocks from 'hooks/useSorting/useSortingAccessProfiles/LockGroupsAndLocks/useSortingAccessProfilesTableLockGroupsAndLocks';
import useSortingMenuAccessProfilesLockGroups from 'hooks/useSortingMenuAccessProfilesLockGroups/useSortingMenuAccessProfilesLockGroups';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useEffect, useRef, useState } from 'react';
import { DoorsState } from 'state/types';
import { Props as IconProps } from 'ui/atoms/Icon/Icon';
import TableCell from 'ui/atoms/TableCell/TableCell';
import TableHeader from 'ui/atoms/TableHeader/TableHeader';
import TableHeaderRow from 'ui/molecules/TableHeaderRow/TableHeaderRow';
import TableRow from 'ui/molecules/TableRow/TableRow';
import Table from 'ui/organisms/Table/Table';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';
import ArrayUtil from 'utils/Array/Array.util';
import { getLockGroupsIconProperties } from 'utils/LockGroupsIcon/LockGroupsIcon.util';
import getLockIconProperties from 'utils/LocksIcon/LocksIcon.util';
import {
  ARROW_DOWN_HEIGHT,
  ARROW_DOWN_WIDTH,
  ARROW_RIGHT_HEIGHT,
  ARROW_RIGHT_WIDTH,
  SecondLineText,
  Variant
} from '../shared';

export interface Props {
  id: string;
  loading: boolean;
  disabled: boolean;
  isCollapsed: boolean;
  handleCollapse: () => void;
  variant: Variant;
  lockGroups: Location[];
  filteredLockGroups: Location[];
  selectedIds: string[];
  disabledIds: string[];
  additionalTexts: SecondLineText[];
  warningTexts: SecondLineText[];
  handleClickTableRow: (lockGroup: Location) => void;
  doors: DoorsState;
  selectedDoorsIds: string[];
}

const AccessProfilesLockGroups: React.FC<React.PropsWithChildren<Props>> = ({
  id,
  loading,
  disabled,
  isCollapsed,
  handleCollapse,
  variant,
  lockGroups,
  filteredLockGroups,
  selectedIds,
  disabledIds,
  additionalTexts,
  warningTexts,
  handleClickTableRow,
  doors,
  selectedDoorsIds
}) => {
  const { translation } = useTranslation();
  const { sorting, handleSortingOnChange, sortingMenuRef } = useSortingAccessProfilesTableLockGroupsAndLocks({
    translation,
    doors,
    lockGroups,
    selectedDoorsIds,
    selectedLocationsIds: selectedIds
  });
  const [dialogData, setDialogData] = useState<Location | null>(null);
  const { showSortingMenu, hideSortingMenu, sortingMenuValue } = useSortingMenuAccessProfilesLockGroups();
  const skeletonArray = ArrayUtil.SkeletonArray();

  const isLockGroupSelected = (lockGroupId: string) => selectedIds.includes(lockGroupId);
  const isLockGroupDisabled = (lockGroupId: string) => disabledIds.includes(lockGroupId);
  const getLockGroupWithAdditionalText = (lockGroupId: string) =>
    additionalTexts.find((lockGroup) => lockGroup.id === lockGroupId)?.text;

  const getLockGroupWithWarningText = (lockGroupId: string) =>
    warningTexts.find((lockGroup) => lockGroup.id === lockGroupId)?.text;

  const getSecondLineText = (lockGroupId: string) =>
    getLockGroupWithAdditionalText(lockGroupId) || getLockGroupWithWarningText(lockGroupId);

  const getIconAndSecondLineTextColor = (lockGroupId: string) =>
    getLockGroupWithWarningText(lockGroupId) ? 'warning' : 'lTextHigh';

  const getFirstLineTextColor = (lockGroupId: string) =>
    variant === 'icon' && isLockGroupSelected(lockGroupId) && !isLockGroupDisabled(lockGroupId)
      ? 'primary'
      : 'lTextHigh';

  const getOnClickIcon = (event: React.MouseEvent<SVGSVGElement, MouseEvent>, lockGroup: Location) => {
    event.stopPropagation();
    setDialogData(lockGroup);
  };

  const getBeforeContentIconProps = (lockGroup: Location): IconProps => ({
    ...getLockGroupsIconProperties(
      isLockGroupSelected(lockGroup.id),
      lockGroup.doors?.length > 0,
      Boolean(getLockGroupWithWarningText(lockGroup.id))
    ),
    onClick: (event) => getOnClickIcon(event, lockGroup)
  });
  const [isVisible, setIsVisible] = useState<boolean | undefined>(undefined);

  const tableHeaderRef = useRef<null | HTMLDivElement>(null);

  const observer = new IntersectionObserver((entries) => {
    const entry = entries[0];
    setIsVisible(entry.isIntersecting);
  });

  if (tableHeaderRef.current) observer.observe(tableHeaderRef.current);

  useEffect(() => {
    if (sortingMenuValue && sortingMenuValue.isMenuOpen && !isVisible) hideSortingMenu();
  }, [hideSortingMenu, isVisible, sortingMenuValue]);

  return (
    <Table
      id={id}
      height={isCollapsed ? '3rem' : 'auto'}
      flex="0 1 auto"
      header={
        !loading ? (
          <div ref={tableHeaderRef}>
            <TableHeaderRow
              disabled={disabled}
              iconAfterHeaderProps={{
                name: 'Filter',
                id: 'sort-locks-button',
                onClick: !disabled
                  ? () =>
                      showSortingMenu({
                        isMenuOpen: true,
                        sorting,
                        sortingMenuRef,
                        sortingMenuIconId: 'sort-locks-button',
                        handleSortingOnChange,
                        variant
                      })
                  : undefined
              }}
              iconAfterHeaderBorder
              id="access-profiles-header-row-locations"
              iconBeforeHeaderProps={{
                name: isCollapsed ? 'ArrowRight' : 'ArrowDown',
                width: isCollapsed ? ARROW_RIGHT_WIDTH : ARROW_DOWN_WIDTH,
                height: isCollapsed ? ARROW_RIGHT_HEIGHT : ARROW_DOWN_HEIGHT,
                color: isCollapsed ? undefined : 'transparent',
                stroke: isCollapsed ? undefined : 'lTextMedium',
                viewBox: isCollapsed ? '0 0 22 45' : undefined,
                onClick: handleCollapse
              }}
            >
              <TableHeader
                headerText={translation.lock_groups}
                superText={variant === 'switch' ? `(${selectedIds.length})` : undefined}
                id="access-profiles-header-locations"
              />
            </TableHeaderRow>
          </div>
        ) : (
          <TableHeaderRow
            disabled={disabled}
            placeForAvatarOrSwitchOrCheckbox={variant === 'icon'}
            id="access-profiles-header-row-lock-groups"
          >
            <TableHeader headerText={translation.lock_groups} id="access-profiles-header-user-groups" />
          </TableHeaderRow>
        )
      }
    >
      {loading
        ? skeletonArray.map((skeletonItem) => (
            <TableRow
              beforeSwitchSkeleton={variant === 'switch'}
              avatarSkeleton={variant === 'icon'}
              selected={false}
              disabled
              id="access-profiles-row-user-groups-loading"
              key={`skeletonTableRow-${skeletonItem.id}`}
            >
              <TableCell isLoading firstLineText="" />
            </TableRow>
          ))
        : !isCollapsed &&
          filteredLockGroups.map((lockGroup, index) => (
            <TableRow
              hoverEffect
              afterSwitchProps={
                variant !== 'switch'
                  ? undefined
                  : {
                      checked: isLockGroupSelected(lockGroup.id),
                      disabled: isLockGroupDisabled(lockGroup.id),
                      onClick: (event) => event.stopPropagation()
                    }
              }
              marginAfterContent={variant === 'switch'}
              beforeContentIconProps={getBeforeContentIconProps(lockGroup)}
              selected={variant === 'icon' ? isLockGroupSelected(lockGroup.id) : false}
              disabled={(variant === 'switch' && disabled) || isLockGroupDisabled(lockGroup.id)}
              onClick={
                !disabled && !isLockGroupDisabled(lockGroup.id) ? () => handleClickTableRow(lockGroup) : undefined
              }
              id={`row-lock-group-${index}`}
              key={lockGroup.id}
            >
              <TableCell
                firstLineText={lockGroup.name}
                firstLineColor={getFirstLineTextColor(lockGroup.id)}
                secondLineText={getSecondLineText(lockGroup.id)}
                secondLineColor={getIconAndSecondLineTextColor(lockGroup.id)}
                key={lockGroup.id}
                isRowWithIconAndSwitch
              />
            </TableRow>
          ))}
      {dialogData && (
        <Modal
          open={!!dialogData}
          onClose={() => setDialogData(null)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box
            id="add-api-key-modal"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: '#252525',
              border: '1px solid #444',
              borderRadius: '4px'
            }}
          >
            <ComponentWrapper width="444px" flexDirection="column">
              <ComponentWrapper padding="1rem" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" color="textPrimary">
                  {translation.locks_in} {dialogData.name}
                </Typography>
                <IconButton onClick={() => setDialogData(null)} style={{ padding: '4px' }}>
                  <CloseOutlined style={{ color: 'rgba(255, 255, 255, 0.56)' }} />
                </IconButton>
              </ComponentWrapper>
              <Divider />
              <ComponentWrapper padding="1rem 0 1rem 1rem">
                <Table>
                  {dialogData.doors.map((door) => (
                    <TableRow
                      beforeContentIconProps={{
                        ...getLockIconProperties.getLockIconProperties(door.type || null),
                        height: 28,
                        width: 28,
                        viewBox: '0 0 32 32'
                      }}
                      id={`row-${id}`}
                      key={door.id}
                    >
                      <TableCell firstLineText={door.name} firstLineId={`row-${id}-name`} />
                    </TableRow>
                  ))}
                </Table>
              </ComponentWrapper>
              <Divider />
              <ComponentWrapper justifyContent="end" height="4rem" padding="0.75rem 1rem">
                <Button onClick={() => setDialogData(null)}>{translation.close}</Button>
              </ComponentWrapper>
            </ComponentWrapper>
          </Box>
        </Modal>
      )}
    </Table>
  );
};

export default AccessProfilesLockGroups;
